const Algo = require('../../msys/msysAV/src_em/algo')
const Draw = require('../../msys/msysAV/src_em/draw')
const msgpack = require('@msgpack/msgpack')
const { toLatLng } = require('../../msys/msysGeo')

function round3(n) {
  n = parseFloat(n)
  if(isNaN(n)) return n
  return Number(n.toFixed(3))
} // round3

function usec2str(usec) {
  if(!(usec>0)) return '-'
  const d = new Date(Math.floor(usec/1000))
  return(
    String(d.getUTCFullYear()).padStart(4, '0') + '-' +
    String(d.getUTCMonth()+1).padStart(2, '0') + '-' +
    String(d.getUTCDate()).padStart(2, '0') + ' ' +
    String(d.getUTCHours()).padStart(2, 0)+':'+
    String(d.getUTCMinutes()).padStart(2, 0)+':'+
    String(d.getUTCSeconds()).padStart(2, 0)+'.'+
    String(usec % 1_000_000).padStart(6, 0)
  )
} // usec2str

function usec6(usec) {
  if(!(usec>0)) return '-'
  return (usec/1_000_000).toFixed(6)
} // usec6

if(!JSON.stringifySafe) {
  JSON.stringifySafe = (obj, indent) => {
    try{
      const str = JSON.stringify(obj, null, indent)
      return str
    }catch(e) {}

    try{
      if(obj && obj instanceof Error) {
        const str = JSON.stringify(obj, Object.getOwnPropertyNames(obj), indent)
        return str
      }
    }catch(e) {}

    try{
      const seen = new WeakSet()
      return JSON.stringify(obj, (key, val) => {
        if(typeof val === 'object' && val !== null) {
          if(seen.has(val)) return
          seen.add(val)
        }
        return val
      }, indent)
    }catch(e) {
      return ''
    }
  } // JSON.stringifySafe
}

class Doppler {
  static APPROACHING = 1
  static NOSPEED = 0
  static RECEDING = -1
  static VEHICLE_WIDTH = 1.8 // average vehicle length in meters
  static VEHICLE_HEIGHT = 1.8 // average vehicle height in meters

  static COLORS = {
    hit: '#aa222d', //'hsl(355, 67%, 40%)',
    hitweak: '#cf7179', //'hsl(355, 49%, 63%)',
    valid: '#308f00', //'hsl(100, 100%, 28%)',
    default: '#22aaaa', //'hsl(180, 67%, 40%)',
  }

  static LEGEND_COLORS = {
    hit: '#aa222d', //'hsl(355, 67%, 40%)',
    hitweak: '#cf7179', //'hsl(355, 49%, 63%)',
    valid: '#308f00', //'hsl(100, 100%, 28%)',
    simulator: 'brown',
    default: '#666666', //'hsl(180, 67%, 40%)',
  }

  channel = 1 // current Doppler channel
  instance = 0 // unique ID of Doppler class instance
  meta = null // current meta information on Doppler configuration (from mmDopplerMeta)
  beam = null // currently active beam (i.e. steady chain of pulses)
  pulses = [] // list of processed pulses
  pulses_queue = [] // list of pulses waiting for processing
  pulse_processing = false // flag if pulse is currently being processed
  nals = [] // list of H264 NAL units
  gpses = [] // list of GPS packets
  sps_set = false // flag if SPS NAL unit has been received
  pps_set = false // flag if PPS NAL unit has been received
  keyframe_set = false // flag if I-frame has been received
  last_end_event_dt = 0 // time of last ended event
  MAX_PULSE_GAP_US = 50_000 // maximum gap between pulses
  MAX_NAL_GAP_US = 200_000 // maximum gap between NAL units
  OBSOLETE_USEC = 20_000_000 // time after which pulses are considered obsolete and removed from this.pulses
  FIX_DISTANCE_LIMIT = 0.1 // relative distance limit for fixing pulse
  FIX_SPEED_LIMIT = 0.2 // relative speed limit for fixing pulse
  STEADY_DISTANCE_LIMIT = 0.5 // distance limit to treat the pulse as part of steady beam
  STEADY_REL_DISTANCE_LIMIT = 0.15 // relative distance limit to treat the pulse as part of steady beam
  STEADY_SPEED_LIMIT = 0.07 // relative speed limit to treat the pulse as part of steady beam
  BEAM_PULSES = 7 // minimal number of pulses in beam to be reported
  RADAR_YAW = 20 // radar yaw angle
  RADAR_H_ANGLE = 7.5 // radar horizontal width angle
  MIN_VEHICLE_LENGTH = 3.5 // minimal vehicle length in meters
  VEHICLE_WIDTH = 1.8 // average vehicle width in meters
  VEHICLE_HEIGHT = 1.8 // average vehicle height in meters
  SHOT_MIN_LONGITUDAL = 5 // minimal longitudal distance for calculating shot_us
  TRIGGER_SIGNAL = 200 // minimal signal level for trigger
  EVENT_SIGNAL = 60 // minimal signal level for event
  EVENT_SIGNAL_MIN_COUNT = 5 // minimal number of pulses with EVENT_SIGNAL level to consider event valid
  GOOD_SIGNAL = 42 // minimal signal level for good pulse
  LOW_SIGNAL = 40 // minimal signal level for low signal pulse
  LANE1_LATERAL = 2.3 // lateral distance of lane 1 from radar
  LANE_WIDTH = 3.5 // width of driving lane in meters
  EVENT_CLOSE_MS = 500 // time after which ended event is closed
  EVENT_CONTEXT_US = 200_000 // time before and after event to be collected as a context
  GPS_CONTEXT_US = 1_000_000 // time before and after event to be searched for GPS packets to calculate event GPS geohash
  GPS_VALIDITY_US = 2_000_000 // time after which GPS packet is considered obsolete

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Class constructor.
  * Creates new instance of Doppler class with unique ID and resets all instance variables.
  */
  constructor(channel=1) {
    this.instance = Date.now()
    this.logger = console
    this.channel = channel
    this.reset()
  } // constructor

  debug(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    this.logger.debug('doppler.js', text)
  } // debug

  log(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    this.logger.log('doppler.js', text)
  } // log

  info(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    this.logger.info('doppler.js', text)
  } // info

  warn(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    this.logger.warn('doppler.js', text)
  } // warn

  error(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    this.logger.error('doppler.js', text)
  } // error

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Callback called for coming individual pulse.
  * Could be overwritten to receive notifications on analysed pulse.
  * @param {object} pulse - pulse object (params of mmDoppler message)
  */
  onPulse() {
    // to be overwritten to receive notifications on analysed pulse
  } // onPulse

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Callback called on each update of current beam (steady chain of pulses).
  * Could be overwritten to receive notifications on updated beam.
  * @param {object} beam - beam object; empty object to end the beam
  * @param {integer} beam.start_idx - index of first pulse of the beam in this.pulses
  * @param {integer} beam.start_us - start time of the beam in microseconds
  * @param {integer} beam.end_idx - index of last pulse of the beam in this.pulses
  * @param {integer} beam.end_us - end time (so far) of the beam in microseconds
  * @param {string} beam.direction - direction of the vehicle: 'approaching' or 'receding'
  * @param {integer} beam.pulse_count - number of pulses in the beam
  * @param {integer} beam.duration - duration of the beam in microseconds
  * @param {number} beam.min_distance - minimal distance to target vehicle in meters
  * @param {number} beam.distance - distance to target vehicle in last beam's pulse in meters
  * @param {number} beam.lateral - lateral distance to target vehicle in meters
  * @param {number} beam.lane - lane number of target vehicle (1, 2 or 3)
  * @param {number} beam.travelled - longitudal distance travelled by target vehicle (so far) in meters
  * @param {number} beam.speed - average speed of target vehicle in m/s
  * @param {number} beam.sog_speed - average speed of measuring vehicle in m/s
  */
  onBeam() {
    // to be overwritten to receive notifications on vehicle in the beam
  } // onBeam

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Callback called when event is finished (i.e. steady chain of pulses is ended).
  * Could be overwritten to receive notifications on ended measurement (hit, nohit, invalid measurement)
  * @param {object} event - event object (doppler_event in MosyMessageTypes.json)
  * @param {string} result - result of the event: 'hit', 'valid', 'invalid', 'ignored', 'dry', 'simulator'
  * @param {string} gps - GPS geohash calculated from GPS packets during (or just before) the event
  * @param {object} meta - meta information on the event (params of mmDopplerMeta message)
  */
  onEvent() {
    // to be overwritten to receive notifications on ended measurement (hit, nohit, invalid measurement)
  } // onEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Callback called when error is detected in processing.
  * Could be overwritten to receive notifications on errors.
  */
  onError() {
    // to be overwritten to receive notifications on errors
  } // onError

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Aborts current processing and resets all instance variables.
  */
  reset(soft_reset) {
    if(this.pulses.length>0) {
      if(soft_reset) {
        this.debug('================== DOPPLER SOFT RESET ==================')
      } else {
        this.warn('================== DOPPLER RESET ==================')
      }
    }

    this.meta = null
    this.pulses = []
    this.pulses_queue = []
    this.last_end_event_dt = 0
    delete this.beam

    if(!soft_reset) {
      this.sps_set = false
      this.pps_set = false
      this.keyframe_set = false
      this.nals = []
      this.gpses = []
    }

    // used to indicate that any current beam has been ended
    this.onBeam({})
  } // reset

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * (Re-)calibrate doppler configuration.
  */
  calibrate(calibration) {
    if(!calibration) {
      this.error('EMPTY DOPPLER CALIBRATE')
      return
    }else if(calibration?.calibration) {
      this.error('INVALID CALL TO DOPPLER CALIBRATE: calibration.calibration')
      return
    }else if(!calibration?.calibration_dt) {
      this.error('INVALID CALIBRATE WITHOUT calibration_dt')
      return
    }
    const { calibration_dt, channel, radar_yaw, radar_h_angle, camera_yaw, remark } = calibration
    const dt = usec2str(calibration_dt)
    this.info(`DOPPLER CALIBRATE ${calibration_dt} ${dt}  channel: ${channel}  radar ${radar_yaw}°  camera ${camera_yaw}° - ${remark}`)
    this.reset()
    this.RADAR_YAW = radar_yaw
    this.RADAR_H_ANGLE = radar_h_angle
  } // calibrate

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert calibration/pulse/H264/GPS message into the processing stream
  */
  insert(message) {
    const { action, params } = message
    if(action==='mmDopplerCalibration') {
      this.calibrate(params.calibration)
    }else if(action==='mmDopplerMeta') {
      this.insertMeta(params)
    }else if(action==='mmDoppler') {
      this.insertPulse(params)
    }else if(action==='mmH264') {
      this.insertNAL(params)
    }else if(action==='mmGps') {
      this.insertGPS(params)
    }
  } // insert

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert Doppler meta information into the processing stream.
  * Expects structure as of mmDopplerMeta message params.
  */
  insertMeta(meta) {
    const old_optics = this.meta?.optics
    this.meta = meta
    const new_optics = meta?.optics
    const event_dt = meta?.event_dt || null

    if(!old_optics) return // already reset
    if(!new_optics) {
      this.onError('optics_missing', { event_dt })
      this.reset()
      return
    }

    // verify if doppler optics did not change
    for(const key of ['channel', 'zoom', 'h_angle', 'v_angle']) {
      if(old_optics[key] !== new_optics[key]) {
        this.onError('optics_changed', { event_dt })
        this.reset()
        return
      }
    }
  } // insertMeta

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert individual Doppler pulse information into the processing stream.
  * Expects structure as of mmDoppler message params.
  */
  insertPulse(pulse) {
    if(this.meta?.limits?.mode==='simulator') {
      if(pulse.sog_speed > 0) {
        pulse.speed += pulse.sog_speed // add speed of the measuring vehicle
        pulse.sog_speed = 0
      }
    }
    this.pulses_queue.push(pulse)
    this.processPulseFromQueue()
  } // insertPulse

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Process one pulse from the queue in blocking manner.
  */
  processPulseFromQueue() {
    if(this.pulse_processing) return
    this.pulse_processing = true
    let pulse = this.pulses_queue.shift()
    if(!pulse) {
      this.pulse_processing = false
      return
    }

    const { event_dt } = pulse
    if(this.pulses.length>0) {
      // verify timing between pulses
      const last_event_dt = this.pulses[this.pulses.length-1]?.event_dt
      const gap = event_dt - last_event_dt
      if(gap < 0) {
        this.onError('pulse_back', { event_dt, last_event_dt })
        this.warn('PULSE BACK between', usec6(last_event_dt), usec2str(last_event_dt), 'and', usec6(event_dt), usec2str(event_dt))
        this.reset()
      }else if(gap > this.MAX_PULSE_GAP_US) {
        this.onError('pulse_gap', { event_dt, last_event_dt, gap })
        this.warn(`PULSE GAP ${gap} ms (>${this.MAX_PULSE_GAP_US}) between`, usec6(last_event_dt), usec2str(last_event_dt), 'and', usec6(event_dt), usec2str(event_dt))
        if(gap<3_000_000) {
          // if the gap is not that big, we can do a soft reset
          // this will not remove NALs and GPSes thus not affecting
          // preceding events awaiting for NALs
          this.reset('soft_reset')
        }else {
          this.reset()
        }
      }
    }

    if(Array.isArray(pulse.targets) && pulse.targets.length>0) {
      // flatten target info to pulse itself: for old version of pulses
      pulse = { ...pulse, ...pulse.targets[0] }
    }

    const obsolete_usec = pulse.event_dt - this.OBSOLETE_USEC
    this.clearPulses(obsolete_usec)

    if(pulse.ignore) {
      this.reset('soft_reset')
    }else {
      this.pulses.push(pulse)
      this.processPulse()
    }

    // unlock processing and try to process next pulse (if exists)
    this.pulse_processing = false
    this.processPulseFromQueue()
  } // processPulseFromQueue

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Remove too old pulses from buffer.
  * Updates current beam pulse indexes to reflect the removal.
  */
  clearPulses(obsolete_usec) {
    const idx = this.pulses.findIndex(p=>p.event_dt>=obsolete_usec)
    if(idx>0) {
      this.pulses.splice(0, idx)
      if(this.beam) {
        if(this.beam.start_idx<idx) {
          // current beam started prior to cleanup; destroy it
          delete this.beam
          this.onBeam({})
        }else {
          // we have to adjust beam indexes when pulses are removed
          this.beam.start_idx -= idx
          this.beam.end_idx -= idx
        }
      }
    }
  } // clearPulses

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert individual H264 video NAL unit into the processing stream.
  * Expects structure as of mmH264 message params.
  */
  insertNAL(nal) {
    const { event_dt, nal_type: type } = nal
    if(this.nals.length>0) {
      // verify timing between NALs
      const last_event_dt = this.nals[this.nals.length-1]?.event_dt
      const gap = event_dt - last_event_dt
      if(gap < 0) {
        this.onError('nal_back', { event_dt, last_event_dt, gap })
        this.warn('NAL BACK', { event_dt, last_event_dt, gap })
        this.reset()
      }else if(gap > this.MAX_NAL_GAP_US) {
        this.onError('nal_gap', { event_dt, last_event_dt, gap })
        this.warn('NAL GAP', { event_dt, last_event_dt, gap })
        this.reset()
      }
    }

    // we ensure there is always SPS > PPS > I > P
    if(type==='SPS') {
      this.nals.push(nal)
      this.sps_set = true
      this.pps_set = false
    }else if(type==='PPS') {
      if(this.sps_set) {
        this.nals.push(nal)
        this.pps_set = true
      }
    }else if(type==='I') {
      if(this.pps_set) {
        this.nals.push(nal)
        this.keyframe_set = true
        const obsolete_usec = nal.event_dt - this.OBSOLETE_USEC
        this.clearNALs(obsolete_usec)
      }
    }else if(type==='P') {
      if(this.keyframe_set) {
        this.nals.push(nal)
      }
    }
  } // insertNAL

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Inform about expected current time.
  * Used for error checking if there is discrepancy between expected and actual time.
  */
  time(event_dt) {
    const errors = []

    // verify Pulses are coming
    if(this.pulses.length===0) {
      errors.push(['pulse_missing', { event_dt }])
    }else {
      const last_event_dt = this.pulses[this.pulses.length-1]?.event_dt
      if(event_dt < last_event_dt) {
        errors.push(['pulse_back', { event_dt, last_event_dt }])
      }else if(event_dt > last_event_dt + this.MAX_PULSE_GAP_US) {
        errors.push(['pulse_missing', { event_dt, last_event_dt }])
      }
    }

    // verify NALs are coming
    if(this.nals.length===0) {
      errors.push(['nal_missing', { event_dt }])
    }else {
      const last_event_dt = this.nals[this.nals.length-1]?.event_dt
      if(event_dt < last_event_dt) {
        errors.push(['nal_back', { event_dt, last_event_dt }])
      }else if(event_dt > last_event_dt + this.MAX_NAL_GAP_US) {
        errors.push(['nal_missing', { event_dt, last_event_dt }])
      }
    }

    if(errors.length>0) {
      this.warn('TIME ERROR')
      this.reset()
      for(const err of errors) this.onError(...err)
    }
  } // time

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Remove NALs from buffer not needed to construct video frame before obsolete time.
  * We find last keyframe preceding the obsoletion coupled with preceding SPS and PPS.
  */
  clearNALs(obsolete_usec) {
    let keyframe_idx = -1
    let sps
    let pps
    let last_sps
    let last_pps
    for(let i=0; i<this.nals.length; i++) {
      const nal = this.nals[i]
      const { nal_type, event_dt } = nal
      if(event_dt>=obsolete_usec) break
      if(nal_type==='SPS') {
        last_sps = nal
        last_pps = null
      }else if(nal_type==='PPS') {
        if(last_sps) last_pps = nal
      }else if(nal_type==='I') {
        if(last_sps && last_pps) {
          keyframe_idx = i
          sps = last_sps
          pps = last_pps
        }
      }
    }
    if(keyframe_idx<0) return
    this.nals = this.nals.slice(keyframe_idx)
    this.nals.unshift(pps)
    this.nals.unshift(sps)
  } // clearNALs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Insert individual GPS packet into the processing stream.
  * Expects structure as of mmGps message params.
  */
  insertGPS(gps) {
    const obsolete_usec = gps.event_dt - this.OBSOLETE_USEC
    this.gpses.push(gps)
    this.gpses = this.gpses.filter(gps=>gps.event_dt>obsolete_usec)
  } // insertGPS

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Process current pulse.
  */
  processPulse() {
    const cnt = this.pulses.length
    if(cnt<4) return
    this.fixPulseTime(cnt-3) // uses 1 pulse before and 2 pulses after
    if(cnt<5) return
    this.fixMissingPulse(cnt-3) // uses 2 pulses before and 2 pulses after
    if(cnt<7) return
    this.calculatePulseDifferentials(cnt-5) // // uses 2 pulses before and 2 pulses after, but all had to be fixed already
    this.onPulse(this.pulses[cnt-5]) // report updated pulse
    this.addPulseToBeam(cnt-5) // try to add pulse to beam if exists, or start new beam
  } // processPulses

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Fix individual pulse time using 1 sibling before and 2 siblings after.
  */
  fixPulseTime(idx) {
    // packets often comes in groups of 2-3 at once from serial link
    // we adjust their event_dt to be in regular intervals
    const a = this.pulses[idx-1].event_dt
    const b = this.pulses[idx].event_dt
    const c = this.pulses[idx+1].event_dt
    const d = this.pulses[idx+2].event_dt

    const d1 = b - a
    const d2 = c - b
    const d3 = d - c

    if(d1 * 3 < d2) {
      // we are short duration followed by long duration
      this.pulses[idx].event_dt_orig = b
      this.pulses[idx].event_dt = Math.round((c+a)/2)
    }else if((d1 + d2) * 2 < d3) {
      // we are first of two short durations followed by long duration
      const delta = Math.round((d-a)/3)
      this.pulses[idx].event_dt_orig = b
      this.pulses[idx].event_dt = a + delta
      this.pulses[idx+1].event_dt_orig = c
      this.pulses[idx+1].event_dt = a + 2*delta
    }
  } // fixPulseTime

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Replaces pulse with low signal level with average of surrounding pulses
  * if they are close enough in distance and speed.
  */
  fixMissingPulse(idx) {
    const GOOD_SIGNAL = this.GOOD_SIGNAL
    const EVENT_SIGNAL = this.EVENT_SIGNAL

    const pulses = this.pulses
    const p = pulses[idx]
    const g = p.signal
    if(g>=GOOD_SIGNAL) return // no need for fixing

    const p1 = pulses[idx-2]
    const p2 = pulses[idx-1]
    const p3 = pulses[idx+1]
    const p4 = pulses[idx+2]

    // check if two pulses before and two after have been (originally !!!) valid
    if(p1.signal < GOOD_SIGNAL) return
    if(p2.signal < GOOD_SIGNAL) return
    if((p3.signal_orig || p3.signal) < GOOD_SIGNAL) return
    if((p4.signal_orig || p4.signal) < GOOD_SIGNAL && (p3.signal_orig || p3.signal) < EVENT_SIGNAL) return

    // verify distances of surrounding pulses are close enough
    const d1 = p1.distance
    const d2 = p2.distance
    const d3 = p3.distance
    const d4 = p4.distance
    const rel_d_diff1 = Math.abs(d1-d2) / (d1+d2) * 2
    if(rel_d_diff1>this.FIX_DISTANCE_LIMIT) return
    const rel_d_diff2 = Math.abs(d2-d3) / (d2+d3) * 2
    if(rel_d_diff2>this.FIX_DISTANCE_LIMIT) return
    const rel_d_diff3 = Math.abs(d3-d4) / (d3+d4) * 2
    if(rel_d_diff3>this.FIX_DISTANCE_LIMIT) return

    // verify speeds of surrounding pulses are close enough
    const s1 = p1.speed
    const s2 = p2.speed
    const s3 = p3.speed
    const s4 = p4.speed
    const rel_s_diff1 = Math.abs(s1-s2) / (s1+s2) * 2
    if(rel_s_diff1>this.FIX_SPEED_LIMIT) return
    const rel_s_diff2 = Math.abs(s2-s3) / (s2+s3) * 2
    if(rel_s_diff2>this.FIX_SPEED_LIMIT) return
    const rel_s_diff3 = Math.abs(s3-s4) / (s3+s4) * 2
    if(rel_s_diff3>this.FIX_SPEED_LIMIT) return

    // surrounding 2+2 pulses are similar enough, so pulse in the middle could be fixed now

    // fix the signal level value
    p.signal_orig = p.signal
    p.signal = GOOD_SIGNAL

    // fix the distance value
    const d_avg = Math.round((d2+d3)/2)
    if(g>=this.LOW_SIGNAL) {
      // pulse with LOW SIGNAL still could sometimes provide good distance information
      const rel_d_diff = Math.abs(p.distance-d_avg) / (p.distance-d_avg) * 2
      if(rel_d_diff > 0.1) {
        // distance in LOW SIGNAL pulse is too different from average of neigbouring distances
        // replace it with their average
        p.distance_orig = p.distance
        p.distance = d_avg
      }
    }else {
      // signal is very low, we can't count on reported distance at all
      // replace it with average of neighbouring distances
      p.distance_orig = p.distance
      p.distance = d_avg
    }

    // fix the speed value
    const s_avg = Math.round((s2+s3)/2)
    if(g>=this.LOW_SIGNAL) {
      // pulse with LOW SIGNAL still could sometimes provide good speed information
      const rel_s_diff = Math.abs(p.speed-s_avg) / (p.speed-s_avg) * 2
      if(rel_s_diff > 0.1) {
        // speed in LOW SIGNAL pulse is too different from average of neigbouring speeds
        // replace it with their average
        p.speed_orig = p.speed
        p.speed = s_avg
      }
    }else {
      // signal is very low, we can't count on reported speed at all
      // replace it with average of neighbouring speeds
      p.speed_orig = p.speed
      p.speed = s_avg
    }
  } // fixMissingPulse

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Calculates relative distance and speed differentials for pulse.
  */
  calculatePulseDifferentials(idx) {
    const pulses = this.pulses
    const p = pulses[idx]

    // infinite relative distance for invalid pulses
    p.rel_distance_diff = Infinity
    p.rel_speed_diff = Infinity

    const GOOD_SIGNAL = this.GOOD_SIGNAL

    const p1 = pulses[idx-2]
    const p2 = pulses[idx-1]
    const p3 = pulses[idx+1]
    const p4 = pulses[idx+2]
    if(p1.signal<GOOD_SIGNAL) return
    if(p2.signal<GOOD_SIGNAL) return
    if(p3.signal<GOOD_SIGNAL) return
    if(p4.signal<GOOD_SIGNAL) return

    // calculate relative distance change using 2 neighbours
    // https://en.wikipedia.org/wiki/Finite_difference_coefficient
    const d1 = p1.distance
    const d2 = p2.distance
    const d3 = p3.distance
    const d4 = p4.distance
    const davg = (d1 + d2 + d3 + d4) / 4
    p.rel_distance_diff = round3((d1 - 8*d2 + 8*d3 - d4) / 12 / davg)
    p.distance_diff = p.rel_distance_diff * davg

    // calculate relative speed change using 2 neighbours
    const s1 = p1.speed
    const s2 = p2.speed
    const s3 = p3.speed
    const s4 = p4.speed
    const direction = Math.sign(p.speed)
    if(direction !== Math.sign(s2) || direction !== Math.sign(s3) || direction !== Math.sign(s4)) return // invalid if not all speeds with the same direction
    const savg = (s1 + s2 + s3 + s4) / 4
    p.rel_speed_diff = round3((s1 - 8*s2 + 8*s3 - s4) / 12 / savg)
  } // calculatePulseDifferentials

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Check if pulse can be added to current beam (steady chain of pulses).
  * If not, close current beam.
  */
  addPulseToBeam(idx) {
    const pulse = this.pulses[idx]
    const { event_dt, rel_speed_diff, rel_distance_diff, distance_diff } = pulse
    if(event_dt < this.last_end_event_dt) return

    const steady_speed = Math.abs(rel_speed_diff) <= this.STEADY_SPEED_LIMIT
    const steady_distance = (
      Math.abs(rel_distance_diff) <= this.STEADY_REL_DISTANCE_LIMIT ||
      Math.abs(distance_diff) < this.STEADY_DISTANCE_LIMIT
    )

    const steady = steady_speed && steady_distance

    if(steady) {
      if(!this.beam) {
        // start new beam; no further processing is needed, as the beam is very short and will not be evaluated neither
        const prolong = this.prolongBeamHead(idx)
        this.beam = { start_idx: idx-prolong }
      }else {
        // extend existing beam
        this.extendBeam(idx)
      }
    }else if(this.beam) {
      this.endBeam(idx) // close current beam
      delete this.beam
    }
  } // addPulseToBeam

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Extend current beam with given pulse, re-analyse it
  * and provide in-progress updates of the potential measurement using onBeam callback
  */
  extendBeam(end_idx) {
    const { start_idx } = this.beam
    const pulse_count = end_idx - start_idx + 1
    if(pulse_count < this.BEAM_PULSES) return // beam does not yet have enough pulses to be even reported

    const start = this.pulses[start_idx]
    const end = this.pulses[end_idx]

    if(!this.beam.start_us) {
      // this is first time beam has enough pulses to be reported
      // calculate some core properties
      this.beam.start_us = start.event_dt
      this.beam.direction = end.speed>=0 ? 'approaching' : 'receding'
    }

    // update beam properties
    this.beam.end_idx = end_idx
    this.beam.end_us = end.event_dt
    this.beam.pulse_count = pulse_count
    this.beam.duration = (end.event_dt - start.event_dt) / 1_000_000

    // calculate longitudal distance travelled by moving object

    // calculate speed
    this.recalculateTravelled(this.beam)
    this.recalculateSpeed(this.beam)

    this.beam.distance = end.distance
    if(!(end.distance >= this.beam.min_distance)) {
      // calculate minimal distances
      this.beam.min_distance = end.distance

      // update lateral distance and lane
      this.beam.lateral = this.beam.min_distance * Math.sin(this.RADAR_YAW / 180 * Math.PI)
      this.beam.lane = Math.max(1, Math.floor((this.beam.lateral - this.LANE1_LATERAL + 1) / this.LANE_WIDTH) + 1)
    }

    // report updated beam
    const event_dt = end.event_dt
    this.onBeam({ ...this.beam, event_dt })
  } // extendBeam

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Calculate result of event depending on its validity current state+limits of measurement.
  */
  eventResult(event, meta) {
    if(event.skip) return 'invalid'
    if(!meta || !meta.optics) return 'invalid'
    if(meta.state!=='on') return 'ignored'
    const { mode, hit_limit, lane_limit, direction_limit } = this.meta.limits
    if(mode==='dry') return 'dry'
    if(mode==='simulator') {
      // in simulator mode we do special checks
      if(!(event.pulse_count >= 25)) return 'invalid'
      if(event.direction!=='approaching') return 'invalid'
      if(event.sog_speed>0) return 'invalid'
      if(event.lane !==1) return 'invalid'
      return 'simulator'
    }
    const { speed, direction, lane } = event
    const speed_kmh = Math.floor(Math.abs(speed * 3.6))
    if(direction_limit==='receding' && direction!=='receding') {
      return 'ignored'
    }else if(direction_limit==='approaching' && direction!=='approaching') {
      return 'ignored'
    }else if(lane===1 && !/1/.test(lane_limit)) {
      return 'ignored'
    }else if(lane===2 && !/2/.test(lane_limit)) {
      return 'ignored'
    }else if(lane===3 && !/3/.test(lane_limit)) {
      return 'ignored'
    }else if(lane!==1 && lane!==2 && lane!==3) {
      return 'ignored'
    }else if(speed_kmh <= hit_limit) {
      return 'valid'
    }else {
      return 'hit'
    }
  } // eventResult

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * End current beam, analyse it and provide final report using onEvent callback.
  * Empty beam is also reported using onBeam callback to inform receiver to clear the beam.
  */
  endBeam(end_idx) {
    let event_dt = this.pulses[end_idx].event_dt
    const beam = this.beam

    const { start_idx } = beam
    const count = end_idx - start_idx + 1
    if(count < this.BEAM_PULSES) {
      this.onBeam({ event_dt })
      return
    }

    // try to prolong by 1 or 2 pulses
    // these may have been omitted just because their differential with following (invalid) pulses failed
    const prolong = this.prolongBeamTail(end_idx)
    for(let i=1; i<=prolong; i++) {
      end_idx++
      this.extendBeam(end_idx)
    }
    this.last_end_event_dt = this.pulses[end_idx+prolong].event_dt

    if(!beam.start_us) {
      // very short beam, event not yet evaluated
      this.onBeam({ event_dt })
      return
    }

    let event
    const skip = this.verifyEvent(beam)
    if(skip) {
      event = { ...beam }
      event.skip = skip
    }else {
      event = this.analyseEvent(beam)
    }

    // delete unused properties
    delete event.start_idx
    delete event.end_idx

    // calculate result of the event
    const meta = this.meta
    const result = this.eventResult(event, meta)

    // calculate GPS for the event
    const gpses = this.getGPSesForEvent(event.start_us - this.GPS_CONTEXT_US, event.end_us + this.GPS_CONTEXT_US)
    const gps = this.getGPSForEvent(gpses, event.shot_us)

    // report event
    this.onEvent(event, result, gps, meta)

    event_dt = this.pulses[end_idx+1].event_dt
    this.onBeam({ event_dt })
  } // endBeam

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Verify if event is valid.
  */
  verifyEvent(event) {
    // very short event, surely not valid
    if(Math.abs(event.travelled)<1) {
      return 'short'
    }

    // quite short beam, probably not complete chain of pulses
    if(event.direction==='approaching' && event.sog_speed<1 && event.speed<6) {
      return 'slow'
    }

    // chain of event pulses must contain some quite high signal pulses
    // or trigger pulse
    const { start_idx, end_idx } = event
    const pulses = this.pulses.slice(start_idx, end_idx+1)
    const event_signal_count = pulses.reduce((count, p) => count + (p.signal>=this.TRIGGER_SIGNAL ? this.EVENT_SIGNAL_MIN_COUNT : p.signal >= this.EVENT_SIGNAL ? 1 : 0), 0)
    if(event_signal_count<this.EVENT_SIGNAL_MIN_COUNT) {
      return 'weak'
    }

    // target is too far away

    // ignore receding targets with speed lower than our own
    // generated by significant static objects/barriers along the road
    if(event.direction==='receding' && Math.abs(event.speed)<event.sog_speed) {
      return 'barrier'
    }

    // ignore receding targets in 3rd lane while we move
    // generated by significant static objects/barriers along the road
    // usually in conjunction with approaching vehicles behind the barrier
    if(event.direction==='receding' && event.lane===3 && event.sog_speed>0) {
      return 'barrier'
    }

    // ignore receding targets that don't have too much decreses of distance
    // as these are most probably barriers
    const distances = pulses.map(p=>p.distance)
    const distances_gauss = Algo.dataGauss1d(distances, 2, 5)
    if(event.direction==='receding' && event.sog_speed>0) {
      const check_idx = Math.floor(pulses.length*0.2)
      let decreases = 0
      let increases = 0
      for(let i=check_idx; i<pulses.length; i++) {
        // let rel_distance_diff = (distances_gauss[i] - distances_gauss[i-1]) * 2 / (distances_gauss[i] + distances_gauss[i-1])
        // if(rel_distance_diff<-0.02) decreases += rel_distance_diff
        const distance_diff = distances_gauss[i] - distances_gauss[i-1]
        if(distance_diff<0) {
          decreases -= distance_diff
        }else {
          increases += distance_diff
        }
      }
      if(increases===0 || (decreases / increases)>0.3) {
        return 'barrier'
      }
    }

    // quite short beam, probably not complete chain of pulses
    if(Math.abs(event.travelled)<4) {
      return 'incomplete'
    }

    // verify reported GPS corresponds to our own speed

    // verify stability of our own speed

    // verify camera zoom has been set correctly as calibrated during the measurement

  // verify we have NALs for the event snapshot
  } // verifyEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Analyse event and calculate all necessary properties.
  */
  analyseEvent(beam) {
    const event = { ...beam }

    this.recalculateSpeed(event)
    this.recalculateLateral(event)
    this.recalculateLongitudalStart(event)
    this.calculateShotTime(event)

    return event
  } // analyseEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculate travelled distance
  * it takes into account also our own speed !!!
  */
  recalculateTravelled(event) {
    const { start_idx, end_idx } = event
    let travelled = 0
    for(let i=start_idx+1; i<=end_idx; i++) {
      const { event_dt, speed, sog_speed } = this.pulses[i]
      const last_event_dt = this.pulses[i-1].event_dt
      travelled += Math.abs(speed + sog_speed) * (event_dt - last_event_dt)
    }
    travelled /= 1_000_000
    this.beam.travelled = round3(travelled)
  } // recalculateTravelled

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculate speed for the event as average of particular speeds
  */
  recalculateSpeed(event) {
    const { start_idx, end_idx } = event
    let moved = 0
    let own = 0
    for(let i=start_idx+1; i<=end_idx; i++) {
      const { event_dt, speed, sog_speed } = this.pulses[i]
      const last_event_dt = this.pulses[i-1].event_dt
      moved += speed * (event_dt - last_event_dt)
      own += sog_speed * (event_dt - last_event_dt)
    }
    moved /= 1_000_000
    own /= 1_000_000
    const avg_speed = moved / event.duration
    const avg_own_speed = own / event.duration
    event.speed = round3(avg_speed)
    event.sog_speed = round3(avg_own_speed)
  } // recalculateSpeed

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated lateral distance
  */
  recalculateLateral(event) {
    const receding = event.direction==='receding'
    const lateral = receding ? this.recalculateLateralReceding(event) : this.recalculateLateralApproaching(event)
    event.lateral = round3(lateral)

    if(isNaN(event.lateral)) this.log('\x1b[31mLATERAL NaN\x1b[0m', event)
  } // recalculateLateral

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated lateral distance for receding vehicles
  */
  recalculateLateralReceding(event) {
    const { start_idx, end_idx } = event
    const pulses = this.pulses.slice(start_idx, end_idx).map(p=>({ ...p }))

    // pre-calculate travelled distance for each pulse
    let travelled = 0
    pulses[0].travelled = 0
    for(let idx=1; idx<pulses.length; idx++) {
      const p = pulses[idx]
      const p2 = pulses[idx-1]
      travelled += Math.abs(p.speed + p.sog_speed) * (p.event_dt - p2.event_dt) / 1_000_000
      p.travelled = travelled
    }

    const laterals = []

    const lateral1 = this.recalculateLateralReceding1(pulses, event)
    if(isNaN(lateral1)) this.log('\x1b[31mRECEDING LATERAL1 NaN\x1b[0m', event)
    else laterals.push(lateral1)

    let lateral = laterals.reduce((a, b)=>a+b, 0) / laterals.length
    lateral = round3(lateral)

    // when the vehicle is passing us very close, minimum distance is unprecise
    // practically there is no way they are closer than 80 cm from the sensor (0.5m from our vehicle side)
    if(lateral<0.8) lateral = 0.8

    return lateral
  } // recalculateLateralReceding

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * 1st method of recalculating estimated lateral distance for receding vehicles
  */
  recalculateLateralReceding1(pulses) {
    // for receding vehicles just slowly passing our vehicle (i.e. low differential speed)
    // it seems sensor is reporting slightly longer distance than should, so we decrease it a bit
    // lateral = Math.min(...distances_gauss) * Math.sin((this.RADAR_YAW + this.RADAR_H_ANGLE/2)/180*Math.PI)
    const distances = pulses.map(p=>p.distance)
    const distances_gauss = Algo.dataGauss1d(distances, 2, 5)
    const min_distance = Math.min(...distances_gauss)
    let lateral = min_distance * Math.sin(this.RADAR_YAW/180*Math.PI) // 0.342
    let avg_diff_speed = 0
    for(const p of pulses) {
      const { sog_speed, speed } = p
      avg_diff_speed += Math.abs(sog_speed + speed)
    }
    avg_diff_speed /= pulses.length
    lateral += Math.max(0, Math.min(0.5, (avg_diff_speed-5) / (20-5) * 0.5)) // add 0-0.5 m for 5 m/s to 20+ m/s

    // when the vehicle is passing us very close, minimum distance is unprecise
    // practically there is no way they are closer than 80 cm from the sensor (0.5m from our vehicle side)
    if(lateral<0.8) lateral = 0.8

    return lateral
  } // recalculateLateralReceding1

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Recalculates estimated lateral distance for approaching vehicles.
  * Major issue is, that Doppler does not catch closest point of approach (CPA).
  * So we have to make some adjustments to the distances reported by the sensor.
  * We use two different approches for better accurcy:
  * - basic calculation using minimal distance
  * - more complex calculation using parabolic fit of distances over travelled distance
  */
  recalculateLateralApproaching(event) {
    const { start_idx, end_idx, speed, sog_speed } = event

    // first special adjustment of incorrectly reported distances
    const diff_speed_kmh = (speed + sog_speed) * 3.6
    const pulses = this.pulses.slice(start_idx, end_idx)

    // pre-calculate travelled distance for each pulse
    let travelled = 0
    pulses[0].travelled = 0
    for(let idx=1; idx<pulses.length; idx++) {
      const p = pulses[idx]
      const p2 = pulses[idx-1]
      travelled += Math.abs(p.speed + p.sog_speed) * (p.event_dt - p2.event_dt) / 1_000_000
      p.travelled = travelled
    }

    const laterals = []

    const lateral1 = this.recalculateLateralApproachingUsingMinDistance(pulses, event)
    if(isNaN(lateral1)) this.log('\x1b[31mAPPROACHING LATERAL1 NaN\x1b[0m', event)
    else laterals.push(lateral1)

    // const lateral2 = isNaN
    // if(isNaN(lateral2)) this.log('\x1b[31mAPPROACHING LATERAL2 NaN\x1b[0m', event)
    // else laterals.push(lateral2)

    const lateral_avg = laterals.reduce((a, b)=>a+b, 0) / laterals.length

    // extra adjustment
    let lateral = 0.892 * lateral_avg - 0.0107 * diff_speed_kmh + 0.56
    lateral = round3(lateral)

    // when the vehicle is passing us very close, minimum distance is unprecise
    // practically there is no way they are closer than 80 cm from the sensor (0.5m from our vehicle side)
    if(lateral<0.8) lateral = 0.8

    return lateral
  } // recalculateLateralApproaching

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Method of recalculating estimated lateral distance for approaching vehicles
  * using minimal distance.
  */
  recalculateLateralApproachingUsingMinDistance(pulses) {
    const distances = pulses.map(p=>p.distance)
    const distances_gauss = Algo.dataGauss1d(distances, 2, 5)
    // ignore initial 10% of pulses, to prevent influence from previous event
    const start_idx = Math.floor(pulses.length * 0.1)
    const distance_min = Math.min(...distances_gauss.slice(start_idx))
    const lateral = distance_min * Math.sin(this.RADAR_YAW/180*Math.PI)
    return lateral
  } // recalculateLateralApproachingUsingMinDistance

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculate index of the pulse that has travelled given distance from the start of the event
  */
  idxTravelledFromStart(event, desired_travelled) {
    const { start_idx, end_idx } = event
    let travelled = 0
    for(let i=start_idx+1; i<=end_idx; i++) {
      const { event_dt, speed, sog_speed } = this.pulses[i]
      const before_dt = this.pulses[i-1].event_dt
      travelled += Math.abs(speed + sog_speed) * (event_dt - before_dt) / 1_000_000
      if(travelled>desired_travelled) return i-1
    }
  } // idxTravelledFromStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculate index of the pulse that has travelled given distance from the end of the event
  */
  idxTravelledFromEnd(event, desired_travelled) {
    const { start_idx, end_idx } = event
    let travelled = 0
    for(let i=end_idx; i>=start_idx+1; i--) {
      const { event_dt, speed, sog_speed } = this.pulses[i]
      const before_dt = this.pulses[i-1].event_dt
      travelled += Math.abs(speed + sog_speed) * (event_dt - before_dt) / 1_000_000
      if(travelled>desired_travelled) return i-1
    }
  } // idxTravelledFromEnd

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated longitudal distance of closest corner of the vehicle at start_us
  */
  recalculateLongitudalStart(event) {
    const receding = event.direction==='receding'
    if(receding) {
      this.recalculateLongitudalStartReceding(event)
    }else {
      this.recalculateLongitudalStartApproaching(event)
    }

    if(isNaN(event.start_longitudal)) {
      this.log('\x1b[31mSTART_LONGITUDAL NaN\x1b[0m', event)
    }
  } // recalculateLongitudalStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated longitudal distance of closest corner of receding vehicle at start_us
  */
  recalculateLongitudalStartReceding(event) {
    const { start_idx, end_idx } = event
    const lateral = event.lateral

    // we expect lateral is quite good
    // we know measured distance is more than it should be (lateral already accounts for that)

    // at distance around 50% reported distance seems to be pretty OK
    // so we can use it to estimate corner pposition at that time and then interpolate to end_us
    const pulses = this.pulses.slice(start_idx, end_idx)
    const distances = pulses.map(p=>p.distance)
    const distances_gauss = Algo.dataGauss1d(distances, 2, 5)
    const distance_min = Math.min(...distances_gauss)
    const distance_max = Math.max(...distances_gauss)
    const distance_mid = (distance_min + distance_max) / 2
    const mid_idx = distances_gauss.findLastIndex(d=>d<=distance_mid)
    const longitudal_mid = Math.sqrt(distances_gauss[mid_idx]**2 - lateral**2)
    let travel_to_end = 0
    for(let i=mid_idx+1; i<pulses.length; i++) {
      const a = pulses[i-1]
      const b = pulses[i]
      travel_to_end += Math.abs(b.speed + b.sog_speed) * (b.event_dt - a.event_dt) / 1_000_000
    }

    const end_longitudal = longitudal_mid + travel_to_end
    const start_longitudal = end_longitudal - event.travelled

    event.start_longitudal = round3(start_longitudal)
  } // recalculateLongitudalStartReceding

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated longitudal distance of closest corner of receding vehicle at start_us
  */
  recalculateLongitudalStartApproaching(event) {
    const { start_idx, end_idx, lateral } = event

    const pulses = this.pulses.slice(start_idx, end_idx)
    let distances = pulses.map(p=>p.distance)
    distances = Algo.dataGauss1d(distances, 2, 5)
    let speeds = pulses.map(p=>p.speed)
    speeds = Algo.dataGauss1d(speeds, 2, 5)
    let sog = pulses.map(p=>p.sog_speed)
    sog = Algo.dataGauss1d(sog, 2, 5)
    let travelled = 0
    const lat2 = lateral * lateral
    const start_longitudals = []
    for(let i=1; i<pulses.length; i++) {
      const p1 = pulses[i-1]
      const p2 = pulses[i]
      const s = speeds[i] + sog[i]
      const t = (p2.event_dt - p1.event_dt) / 1_000_000
      let d = distances[i]
      d = d - lateral * 0.8 - 0.5
      travelled += Math.abs(s * t)
      pulses[i].distance_gauss = d
      pulses[i].speed_gauss = s
      pulses[i].travelled = travelled

      const d2 = d * d
      if (d2 <= lat2) continue

      const along = Math.sqrt(d2 - lat2)
      const start_longitudal = travelled + along
      start_longitudals.push(start_longitudal)
    }

    start_longitudals.sort((a, b) => a - b)
    const start_longitudal = start_longitudals[Math.floor(start_longitudals.length / 2)]

    event.start_longitudal = round3(start_longitudal)
  } // recalculateLongitudalStartApproaching

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculates estimated longitudal distance of closest corner of receding vehicle at start_us
  */
  recalculateLongitudalStartApproaching1(pulses, event) {
    const { lateral } = event
    const radar_center_longitudal = lateral / Math.tan(this.RADAR_YAW*Algo.degrad)
    const start_longitudal = radar_center_longitudal + 1.2 + lateral * 0.5
    return start_longitudal
  } // recalculateLongitudalStartApproaching1

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculate best time for generating event snapshot
  * tries to find the time when front (for approaching) or rear (for receding) license plate is in middle of beam
  */
  calculateShotTime(event, pulses) {
    if(!pulses) {
      const { start_idx, end_idx } = event
      pulses = this.pulses.slice(start_idx, end_idx+1)
    }

    const { lateral, start_longitudal } = event

    event.shot_us = pulses[Math.round(pulses.length/2)]?.event_dt // default is middle of measurement
    if(isNaN(lateral) || isNaN(start_longitudal)) return

    // calculate target longitudal distance for shot time
    // align with middle of radar beam
    let desired_longitudal = lateral / Math.tan(this.RADAR_YAW/180*Math.PI)
    if(desired_longitudal < this.SHOT_MIN_LONGITUDAL) desired_longitudal = this.SHOT_MIN_LONGITUDAL

    if(event.direction==='approaching') {
      let longitudal = start_longitudal
      let idx = 0
      while(idx<pulses.length-1 && longitudal>desired_longitudal) {
        idx++
        const a = pulses[idx-1]
        const b = pulses[idx]
        const speed = Math.abs(b.speed + b.sog_speed)
        const travelled = speed * (b.event_dt - a.event_dt) / 1_000_000
        longitudal -= travelled
      }
      event.shot_us = pulses[idx].event_dt
    }else if(event.direction==='receding') {
      let longitudal = start_longitudal
      let idx = 0
      while(idx<pulses.length-1 && longitudal<desired_longitudal) {
        idx++
        const a = pulses[idx-1]
        const b = pulses[idx]
        const speed = Math.abs(b.speed + b.sog_speed)
        const travelled = speed * (b.event_dt - a.event_dt) / 1_000_000
        longitudal += travelled
      }
      event.shot_us = pulses[idx].event_dt
    }
  } // calculateShotTime

  getBinaryDataForEvent(event) {
    const { start_us, end_us } = event
    const first_us = start_us - this.EVENT_CONTEXT_US
    const last_us = end_us + this.EVENT_CONTEXT_US

    let pulses = this.getPulsesForEvent(first_us, last_us)
    pulses = Doppler.serializePulses(pulses)

    let nals = this.getNALsForEvent(first_us, last_us)
    nals = Doppler.serializeNALs(nals)

    let gpses = this.getGPSesForEvent(event.start_us - this.GPS_CONTEXT_US, event.end_us + this.GPS_CONTEXT_US)
    const gpses_count = gpses.length
    gpses = Doppler.serializeGPSes(gpses)

    return { pulses, nals, gpses, gpses_count }
  } // getBinaryDataForEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieve H264 NAL units needed to construct snapshots for given time frame
  */
  getNALsForEvent(first_us, last_us) {
    let start = this.nals.findLastIndex(nal=>nal.nal_type==='SPS' && nal.event_dt<=first_us)
    if(start<0) start = this.nals.findIndex(nal=>nal.nal_type==='SPS')
    if(start<0) return []
    const end = this.nals.findLastIndex(nal=>(nal.nal_type==='I' || nal.nal_type==='P') && nal.event_dt<=last_us)
    if(end<start) return []
    return this.nals.slice(start, end+1)
  } // getNALsForEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieve Doppler pulses for given time frame
  */
  getPulsesForEvent(first_us, last_us) {
    const pulses = this.pulses.filter(p=>first_us<=p.event_dt && p.event_dt<=last_us)
    return pulses
  } // getPulsesForEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieve GPS records for given time frame including closest preceding the start
  */
  getGPSesForEvent(first_us, last_us) {
    let start = this.gpses.findLastIndex(gps=>gps.event_dt<=first_us && (first_us-gps.event_dt)<this.GPS_VALIDITY_US)
    if(start<0) start = this.gpses.findIndex(gps=>gps.event_dt>=first_us && gps.event_dt<=last_us)
    if(start<0) return []
    let end = this.gpses.findIndex(gps=>gps.event_dt>=last_us && (gps.event_dt-last_us)<this.GPS_VALIDITY_US)
    if(end<0) end = this.gpses.findLastIndex(gps=>gps.event_dt<=last_us)
    if(end<start) return []
    return this.gpses.slice(start, end+1)
  } // getGPSesForEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieve GPS records for given time frame including closest preceding the start
  */
  getGPSForEvent(gpses, shot_us) {
    const after_idx = gpses.findIndex(gps=>gps.event_dt>=shot_us)
    if(after_idx<0) {
      const before_idx = gpses.findLastIndex(gps=>gps.event_dt<=shot_us)
      if(before_idx<0) return
      return gpses[before_idx]?.gps
    }
    let gps
    if(after_idx===0) {
      gps = gpses[after_idx]
    }else {
      const before_diff = Math.abs(gpses[after_idx-1].event_dt - shot_us)
      const after_diff = Math.abs(gpses[after_idx].event_dt - shot_us)
      gps = gpses[before_diff<=after_diff ? after_idx-1 : after_idx]
    }
    return gps?.gps
  } // getGPSesForEvent

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieve H264 NAL units needed to construct snapshot at given time
  */
  getNALsForSnapshot(us, nals) {
    if(!(us>0)) return
    if(!nals) nals = this.nals
    const start = nals.findLastIndex(nal=>nal.nal_type==='SPS' && nal.event_dt<=us)
    if(start<0) {
      const us1 = nals[0]?.event_dt
      const us2 = nals[nals.length-1]?.event_dt
      this.debug('No SPS before shot_us', usec6(us), usec2str(us), `${nals.length} NALs:`, usec6(us1), usec2str(us1), '-', usec6(us2), usec2str(us2))
      return
    }

    const before = nals.findLastIndex(nal=>(nal.nal_type==='I' || nal.nal_type==='P') && nal.event_dt<=us, start)
    if(before<0) {
      const us1 = nals[0]?.event_dt
      const us2 = nals[nals.length-1]?.event_dt
      this.error('No frame before shot_us', usec6(us), usec2str(us), `${nals.length} NALs:`, usec6(us1), usec2str(us1), '-', usec6(us2), usec2str(us2))
      return
    }

    let after = nals.findIndex(nal=>(nal.nal_type==='I' || nal.nal_type==='P') && nal.event_dt>=us, before)
    if(after<0) after=before
    if(after===before) {
      return nals.slice(start, before+1)
    }else if(Math.abs(nals[before].event_dt-us) <= Math.abs(nals[after].event_dt-us)) {
      return nals.slice(start, before+1)
    }else {
      return nals.slice(start, after+1)
    }
  } // getNALsForSnapshot

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * try to prolong start of beam by 1 or 2 pulses
  * which might be excluded because their differential failed
  * returns number of pulses to be extended
  */
  prolongBeamHead(start_idx) {
    const pulses = this.pulses
    const GOOD_SIGNAL = this.GOOD_SIGNAL
    const a = pulses[start_idx+2]
    const b = pulses[start_idx+1]
    const c = pulses[start_idx]
    const d = pulses[start_idx-1]
    const e = pulses[start_idx-2]

    if(d.signal<GOOD_SIGNAL) return 0

    // interpolate distance using last 3 pulses
    const { mx: d_mx, bx: d_bx } = Algo.linearRegression([
      [a.event_dt - a.event_dt, a.distance],
      [b.event_dt - a.event_dt, b.distance],
      [c.event_dt - a.event_dt, c.distance],
    ])

    const d_distance_est = round3((d.event_dt-a.event_dt) * d_mx + d_bx)
    if(Math.abs(d_distance_est-d.distance)*2/(d_distance_est+d.distance) > 0.1) return 0

    // interpolate distance using last 3 pulses
    const { mx: s_mx, bx: s_bx } = Algo.linearRegression([
      [a.event_dt - a.event_dt, a.speed],
      [b.event_dt - a.event_dt, b.speed],
      [c.event_dt - a.event_dt, c.speed],
    ])

    const d_speed_est = round3((d.event_dt-a.event_dt) * s_mx + s_bx)
    if(Math.abs(d_speed_est-d.speed)*2/(d_speed_est+d.speed) > 0.1) return 0

    if(e.signal<GOOD_SIGNAL) return 1

    const e_distance_est = round3((e.event_dt-a.event_dt) * d_mx + d_bx)
    if(Math.abs(e_distance_est-e.distance)*2/(e_distance_est+e.distance) > 0.1) return 1

    const e_speed_est = round3((e.event_dt-a.event_dt) * s_mx + s_bx)
    if(Math.abs(e_speed_est-e.speed)*2/(e_speed_est+e.speed) > 0.1) return 1

    return 2
  } // prolongBeamHead

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * try to prolong end of beam by 1 or 2 pulses
  * which might be excluded because their differential failed
  * returns number of pulses to be extended
  */
  prolongBeamTail(end_idx) {
    const pulses = this.pulses
    const GOOD_SIGNAL = this.GOOD_SIGNAL
    const a = pulses[end_idx-2]
    const b = pulses[end_idx-1]
    const c = pulses[end_idx]
    const d = pulses[end_idx+1]
    const e = pulses[end_idx+2]

    if(d.signal<GOOD_SIGNAL) return 0

    // interpolate distance using last 3 pulses
    const { mx: d_mx, bx: d_bx } = Algo.linearRegression([
      [a.event_dt - a.event_dt, a.distance],
      [b.event_dt - a.event_dt, b.distance],
      [c.event_dt - a.event_dt, c.distance],
    ])

    const d_distance_est = round3((d.event_dt-a.event_dt) * d_mx + d_bx)
    if(Math.abs(d_distance_est-d.distance)*2/(d_distance_est+d.distance) > 0.1) return 0

    // interpolate speed using last 3 pulses
    const { mx: s_mx, bx: s_bx } = Algo.linearRegression([
      [a.event_dt - a.event_dt, a.speed],
      [b.event_dt - a.event_dt, b.speed],
      [c.event_dt - a.event_dt, c.speed],
    ])

    const d_speed_est = round3((d.event_dt-a.event_dt) * s_mx + s_bx)
    if(Math.abs(d_speed_est-d.speed)*2/(d_speed_est+d.speed) > 0.1) return 0

    if(e.signal<GOOD_SIGNAL) return 1

    const e_distance_est = round3((e.event_dt-a.event_dt) * d_mx + d_bx)
    if(Math.abs(e_distance_est-e.distance)*2/(e_distance_est+e.distance) > 0.1) return 1

    const e_speed_est = round3((e.event_dt-a.event_dt) * s_mx + s_bx)
    if(Math.abs(e_speed_est-e.speed)*2/(e_speed_est+e.speed) > 0.1) return 1

    return 2
  } // prolongBeamTail

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns color appropriate for type of result
  */
  static color(params) {
    const result = typeof params==='string' ? params : params?.result
    const color = Doppler.COLORS[result] || Doppler.COLORS.default
    return color
  } // color

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns background color appropriate for type of result
  */
  static background(params) {
    const result = typeof params==='string' ? params : params?.result
    const color = Doppler.LEGEND_COLORS[result] || Doppler.LEGEND_COLORS.default
    return color
  } // background

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns foreground color appropriate for type of result
  */
  static foreground() {
    return '#FFFFFF'
  } // foreground

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * estimates position of the vehicle at given time
  * @param {Object} calibration - calibration data
  * @param {Object} event - event data
  * @param {number} usec - time in microseconds to estimate position for
  * @returns {Object} - estimated position with travelled distance, lateral and longitudal distances, and total distance
  * @property {number} travelled - distance travelled by the vehicle in meters
  * @property {number} lateral - lateral distance from the sensor in meters
  * @property {number} longitudal - longitudal distance from the sensor in meters
  * @property {number} distance - total distance from the sensor in meters
  */
  static estimatePosition(calibration, event, usec) {
    const radar_yaw_deg = calibration.radar_yaw/180*Math.PI
    let { shot_us, start_us, speed, sog_speed, lateral, start_longitudal } = event
    usec = parseInt(usec)
    shot_us = parseInt(shot_us)
    start_us = parseInt(start_us)
    speed = parseFloat(speed)
    sog_speed = parseFloat(sog_speed)
    lateral = parseFloat(lateral)
    start_longitudal = parseFloat(start_longitudal)
    const diff_speed = (speed + sog_speed)
    let longitudal
    let travelled = Math.abs(diff_speed) * (usec - start_us) / 1_000_000
    if(isNaN(start_longitudal)) {
      const travelled_from_shot = -diff_speed * (usec - shot_us) / 1_000_000
      longitudal = lateral / Math.tan(radar_yaw_deg)
      if(usec>0) longitudal += travelled_from_shot
    }else {
      longitudal = start_longitudal + (event.direction==='receding' ? travelled : -travelled)
    }
    travelled = Math.abs(travelled)
    const distance = Math.sqrt(longitudal**2 + lateral**2)
    return { travelled, lateral, longitudal, distance }
  } // estimatePosition

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculates bounding rectangle for event
  */
  static getBoundaries(rig, event, usec, ratio, enlarge={}) {
    if(!(usec>0)) return
    if(!rig) return
    const { zoom, h_pixels, v_pixels, camera_height, camera_radar_side, camera_radar_forward, project } = rig

    const enlarge_v_lateral_zoom_ratio = parseFloat(enlarge?.v_lateral_zoom_ratio) || 0
    let enlarge_v_meters = parseFloat(enlarge?.v_meters) || 0
    enlarge_v_meters += (event?.lateral || 0) / zoom * enlarge_v_lateral_zoom_ratio
    const enlarge_top_meters = parseFloat(enlarge?.top_meters) || 0
    const enlarge_bottom_meters = parseFloat(enlarge?.bottom_meters) || 0

    const enlarge_h_px = parseInt(enlarge?.h_px) || 0
    const enlarge_v_px = parseInt(enlarge?.v_px) || 0
    const enlarge_left_px = parseInt(enlarge?.left_px) || 0
    const enlarge_right_px = parseInt(enlarge?.right_px) || 0
    const enlarge_top_px = parseInt(enlarge?.top_px) || 0
    const enlarge_bottom_px = parseInt(enlarge?.bottom_px) || 0

    const enlarge_h_percent = parseFloat(enlarge?.h_percent) || 0
    const enlarge_v_percent = parseFloat(enlarge?.v_percent) || 0
    const enlarge_left_percent = parseFloat(enlarge?.left_percent) || 0
    const enlarge_right_percent = parseFloat(enlarge?.right_percent) || 0
    const enlarge_top_percent = parseFloat(enlarge?.top_percent) || 0
    const enlarge_bottom_percent = parseFloat(enlarge?.bottom_percent) || 0

    const { lateral, longitudal } = Doppler.estimatePosition(rig, event, usec)

    const LAT = -(lateral + camera_radar_side)
    const HEIGHT1 = -camera_height - enlarge_v_meters - enlarge_bottom_meters
    const HEIGHT2 = -camera_height + this.VEHICLE_HEIGHT + enlarge_v_meters + enlarge_top_meters
    const LON = longitudal + camera_radar_forward
    const b = project([LON, LAT, HEIGHT1], true)
    const t = project([LON, LAT, HEIGHT2], true)
    const h = b[1] - t[1] + 1
    const h4 = Math.round(h / 4)
    const tl = [t[0] - h4*3.5, t[1]]
    const br = [b[0] + h4*2.5, b[1]]

    const e_width = br[0]-tl[0]+1
    const e_height = br[1]-tl[1]+1
    const enlarge_h = e_width * enlarge_h_percent / 100 + enlarge_h_px
    const enlarge_v = e_height * enlarge_v_percent / 100 + enlarge_v_px
    const enlarge_left = e_width * enlarge_left_percent / 100 + enlarge_left_px
    const enlarge_right = e_width * enlarge_right_percent / 100 + enlarge_right_px
    const enlarge_top = e_height * enlarge_top_percent / 100 + enlarge_top_px
    const enlarge_bottom = e_height * enlarge_bottom_percent / 100 + enlarge_bottom_px
    let x1 = Math.round(tl[0] - enlarge_h - enlarge_left)
    let x2 = Math.round(br[0] + enlarge_h + enlarge_right)
    let y1 = Math.round(tl[1] - enlarge_v - enlarge_top)
    let y2 = Math.round(br[1] + enlarge_v + enlarge_bottom)
    let width = x2-x1+1
    let height = y2-y1+1

    if(ratio>0) {
      if(width < height * ratio) {
        width = Math.floor(height * ratio)
        const centerX = (x1 + x2) / 2
        x1 = Math.round(centerX - width / 2)
        x2 = Math.round(centerX + width / 2)
      }else {
        height = Math.floor(width / ratio)
        const centerY = (y1 + y2) / 2
        y1 = Math.round(centerY - height / 2)
        y2 = Math.round(centerY + height / 2)
      }

      let stop = 10
      while(stop>0 && (x1<0 || x2>=h_pixels || y1<0 || y2>=v_pixels)) {
        stop--
        if(x1<0) {
          x2 += -x1
          x1 = 0
        }
        if(x2>=h_pixels) {
          x1 -= x2 - h_pixels + 1
          if(x1<0) x1 = 0
          x2 = h_pixels - 1
        }
        if(y1<0) {
          y2 += -y1
          y1 = 0
        }
        if(y2>=v_pixels) {
          y1 -= y2 - v_pixels + 1
          if(y1<0) y1 = 0
          y2 = v_pixels - 1
        }

        width = x2 - x1 + 1
        height = y2 - y1 + 1
        if(width < height * ratio) {
          width = Math.floor(height * ratio)
          const centerX = (x1 + x2) / 2
          x1 = Math.round(centerX - width / 2)
          x2 = Math.round(centerX + width / 2)
        }else {
          height = Math.floor(width / ratio)
          const centerY = (y1 + y2) / 2
          y1 = Math.round(centerY - height / 2)
          y2 = Math.round(centerY + height / 2)
        }
      }
    } // ratio>0

    const rect = { x1, y1, x2, y2, width, height }
    return rect
  } // getBoundaries

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * retrieves latest calibration before given time
  */
  static getLatestCalibration = async function(listener, usec, validity_sec=31536000) {
    try{
      const start_usec = (Math.floor(usec/1_000_000) - validity_sec) * 1_000_000
      const msg = await listener.findMessage(start_usec, usec, 'backward', 'mmDopplerCalibration', [['KEEP', '.calibration.calibration_dt~\\d+']], 'dopplercalibration')
      if(msg?.action !== 'mmDopplerCalibration') return
      if(!msg.params?.identity || typeof msg.params.identity!=='object') return
      if(!msg.params?.calibration || typeof msg.params.calibration!=='object') return
      delete msg.params.calibration.camera_jpeg
      const calibration_dt = parseInt(msg.params.calibration.calibration_dt)
      if(!(calibration_dt>0)) return
      return msg
    }catch(e) {
      listener.error('getLatestCalibration', e)
    }
  } // getLatestCalibration

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * constructs calibration image with grid and zoomed calibration points
  */
  static async getCalibrationImage(src, calibration, identity, legend_lang, q=0.8) {
    let canvas_image
    try{
      canvas_image = (await Algo.canvasFrom(src, true))[0]
    }catch(e) {
    }
    if(!canvas_image) return
    const ctx_image = canvas_image.getContext('2d')

    // prepare new canvas, bit wider than original image to be able to show vanishing point
    const canvas = document.createElement('canvas')
    canvas.width = canvas_image.width
    canvas.height = canvas_image.height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(canvas_image, 0, 0)

    // draw grid with labels beyond calibration area
    const optics = Doppler.zoomOptics(calibration, calibration.zoom)
    const rig = Algo.rig(calibration, optics)
    Doppler.drawGrid(ctx, rig)

    // draw calibration lines and markers
    Doppler.drawHorizon(ctx, rig, '#0396df', 10)
    Doppler.drawCalibrationMarkers(ctx, ctx_image, rig)
    Doppler.drawCalibrationLines(ctx, rig)

    // draws legend with calibration info onto the canvas
    // will enlarge height of the canvas
    if(identity && legend_lang) {
      const canvas_jpeg = Doppler.drawCalibrationLegend(ctx, calibration, identity, legend_lang)
      return canvas_jpeg.toDataURL('image/jpeg', q)
    }else {
      return ctx.canvas.toDataURL('image/jpeg', q)
    }
  } // getCalibrationImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculates optics for given zoom using calibration.zooms
  */
  static zoomOptics(calibration, zoom) {
    if(!calibration || typeof calibration!=='object') return

    let { h_pixels, v_pixels, zooms } = calibration
    h_pixels = parseInt(h_pixels)
    v_pixels = parseInt(v_pixels)
    if(!(h_pixels>0 && v_pixels>0)) return

    const angles = Algo.anglesForZoom(zooms, zoom)
    if(!angles) return
    delete angles.z0
    delete angles.z1
    delete angles.z2

    return { h_pixels, v_pixels, ...angles }
  } // zoomOptics

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * calculates camera and rotation matrix for given zoom
  * using calibration.zooms and other calibration parameters
  */
  static zoomMatrices(calibration, zoom) {
    const optics = Doppler.zoomOptics(calibration, zoom)
    if(!optics) return
    const { h_pixels, v_pixels, h_angle, v_angle } = optics
    let { camera_height, camera_yaw, camera_pitch, camera_roll } = calibration
    camera_height = parseFloat(camera_height)
    camera_yaw = parseFloat(camera_yaw)
    camera_pitch = parseFloat(camera_pitch)
    camera_roll = parseFloat(camera_roll)
    if(!(camera_height>=0) || isNaN(camera_yaw) || isNaN(camera_pitch) || isNaN(camera_roll)) return

    const cameraMatrix = Algo.cameraMatrix(h_pixels, v_pixels, h_angle, v_angle)
    const rotationMatrix = Algo.rotationMatrix(camera_yaw, camera_pitch, camera_roll)

    return { h_pixels, v_pixels, zoom, h_angle, v_angle, camera_height, camera_yaw, camera_pitch, camera_roll, cameraMatrix, rotationMatrix }
  } // zoomMatrices

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw grid lines with optional labels onto canvas using calibration and matrices
  */
  static drawGrid(ctx, rig, color='#00ffffcc', thick1=1, thick2=2) {
    if(!(ctx instanceof CanvasRenderingContext2D)) return
    if(!rig || typeof rig!=='object') return

    const { camera_height, camera_side, camera_forward, camera_lon, camera_lat2, camera_yaw, project } = rig
    if(!(camera_height>=0) || isNaN(camera_side) || isNaN(camera_forward) || isNaN(camera_yaw)) return

    const label_lat = -camera_lat2 - 0.5
    const label_lon = camera_lon + 1.5

    const LAT_MIN = 1
    const LAT_MAX = 10
    const LON_MIN = 1
    const LON_MAX = 25
    const GROUND = -camera_height

    color = color || '#00ffffcc'

    // display grid lines with distance labels
    for(let car_lat = 0; car_lat <= LAT_MAX; car_lat++) {
      const camera_lat = -(car_lat + camera_side)
      const p = project([1, camera_lat, GROUND], true)
      const p2 = project([1_000_000, camera_lat, GROUND], true)
      Draw.drawLine(ctx, ...p, ...p2, color, thick1)

      if(!isNaN(label_lon)) {
        const [x, y] = project([label_lon, camera_lat, GROUND], true) || [undefined, undefined]
        Draw.drawText(ctx, `${car_lat} m`, 28, x, y, 'center', color, color, 1)
      }
    }

    for(let car_lon = LON_MIN; car_lon <= LON_MAX; car_lon += 1) {
      const camera_lon = car_lon + camera_forward
      const p = project([camera_lon, -LAT_MIN + 1, GROUND], true)
      const p2 = project([camera_lon, -LAT_MAX - 1, GROUND], true)
      Draw.drawLine(ctx, ...p, ...p2, color, car_lon % 5 === 0 ? thick2 : thick1)

      if(!isNaN(label_lat) && car_lon % 5 === 0) {
        const [x, y] = project([camera_lon, label_lat, GROUND], true) || [undefined, undefined]
        Draw.drawText(ctx, `${car_lon} m`, 28, x, y, 'center', color, color, 1)
      }
    }
  } // drawGrid

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw horizon line
  */
  static drawHorizon(ctx, rig, color='#0396dfcc', thick=5) {
    if(!(ctx instanceof CanvasRenderingContext2D)) return
    if(!rig || typeof rig!=='object') return

    const { camera_height, project } = rig
    if(!(camera_height>=0) || !project) return

    const h1 = project([1000000, -1000000, -camera_height], true)
    const h2 = project([1000000, -1, -camera_height], true)
    Draw.drawLine(ctx, ...h1, ...h2, color, thick)
  } // drawHorizon

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw calibration points onto canvas using calibration and matrices
  */
  static drawCalibrationLines(ctx, rig, color='#ffd900cc', thick=3, label_size=28) {
    if(!(ctx instanceof CanvasRenderingContext2D)) return
    if(!rig || typeof rig!=='object') return

    const { h_pixels, v_pixels, camera_height, camera_side, camera_forward, camera_lon, camera_lat, camera_lat2, project } = rig

    const center_color = '#ff44ffaa'
    Draw.drawLine(ctx, h_pixels/2, 0, h_pixels/2, v_pixels, center_color, thick)
    Draw.drawLine(ctx, 0, v_pixels/2, h_pixels, v_pixels/2, center_color, thick)

    const lat0 = -camera_side
    const lon1 = 1
    const lon2 = camera_lon - 0.3
    const lon3 = camera_lon + 0.3
    const a1 = project([lon1, lat0, -camera_height], true)
    const a2 = project([camera_lon, lat0, -camera_height], true)
    const a3 = project([100000, lat0, -camera_height], true)
    const b1 = project([lon2, -camera_lat2, -camera_height], true)
    const b2 = project([camera_lon, -camera_lat2, -camera_height], true)
    const b3 = project([lon3, -camera_lat2, -camera_height], true)
    const b4 = project([camera_lon, -camera_lat2, 0], true)
    const c1 = project([lon2, -camera_lat, -camera_height], true)
    const c2 = project([camera_lon, -camera_lat, -camera_height], true)
    const c3 = project([lon3, -camera_lat, -camera_height], true)
    const c4 = project([camera_lon, -camera_lat, 0], true)

    Draw.drawLine(ctx, ...a1, ...a3, color, thick)
    Draw.drawLine(ctx, ...a2, ...b2, color, thick)
    Draw.drawLine(ctx, ...b1, ...b3, color, thick)
    Draw.drawLine(ctx, ...b2, ...b4, color, thick)
    Draw.drawLine(ctx, ...b2, ...c2, color, thick)
    Draw.drawLine(ctx, ...c1, ...c3, color, thick)
    Draw.drawLine(ctx, ...c2, ...c4, color, thick)

    ctx.font = label_size+'px Helvetica'

    ctx.fillStyle = color
    ctx.strokeStyle = color
    ctx.lineWidth = 1

    const between = (a, b) => {
      return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
    }

    const drawText = (p, text, align, baseline) => {
      ctx.textAlign = align || 'center'
      ctx.textBaseline = baseline || 'middle'
      ctx.fillText(text, ...p)
      ctx.strokeText(text, ...p)
    }

    const car_lon = camera_lon - camera_forward
    const car_lat2 = camera_lat2 - camera_side
    const car_lat = camera_lat - camera_side
    const line1 = Algo.clipLine(...a1, ...a2, 0, 0, h_pixels, v_pixels, true)
    if(line1) {
      const [x1, y1, x2, y2] = line1
      const [x, y] = between([x1, y1], [x2, y2])
      drawText([x - 10, y], `${(car_lon).toFixed(3)} m`, 'right', 'bottom')
    }

    const line2 = Algo.clipLine(...a2, ...b2, 0, 0, h_pixels, v_pixels, true)
    if(line2) {
      const [x1, y1, x2, y2] = line2
      const [x, y] = between([x1, y1], [x2, y2])
      drawText([x, y+10], `${(car_lat2).toFixed(3)} m`, 'center', 'top')
    }

    {
      const [x, y] = between(b2, c2)
      drawText([x, y+10], `${(car_lat - car_lat2).toFixed(3)} m`, 'center', 'top')
    }

    {
      const [x, y] = between(c2, c4)
      drawText([x - 10, y], `${(camera_height).toFixed(3)} m`, 'right')
    }
  } // drawCalibrationLines

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw calibration markers with zoomed clips onto canvas
  */
  static drawCalibrationMarkers(ctx, ctx_image, rig, points) {
    if(!(ctx_image instanceof CanvasRenderingContext2D)) return
    if(!(ctx instanceof CanvasRenderingContext2D)) return
    if(!rig) return

    const { h_pixels, v_pixels, camera_x, camera_y, camera_x2, camera_y2 } = rig
    const new_x = parseInt(points?.[0]?.[0])
    const new_y = parseInt(points?.[0]?.[1])
    const new_x2 = parseInt(points?.[1]?.[0])
    const new_y2 = parseInt(points?.[1]?.[1])

    const dst_r = Math.round(v_pixels / 5)
    const src_r = Math.round(dst_r / 3)

    const markers = [
      [src_r, camera_x, camera_y, new_x, new_y, dst_r, dst_r*2, dst_r*1.05],
      [src_r, camera_x2, camera_y2, new_x2, new_y2, dst_r, h_pixels-dst_r*2-1, dst_r*1.05],
    ]

    for(const [, x, y, new_x, new_y, , dst_x, dst_y] of markers) {
      const adjusted = new_x >= 0 && new_x < h_pixels && new_y >= 0 && new_y < v_pixels
      const color = !points ? '#ffd900' : adjusted ? '#1aff00' : '#ff000099'

      // define circular clipping region on target
      ctx.save()
      ctx.beginPath()
      ctx.arc(dst_x, dst_y, dst_r, 0, 2 * Math.PI)
      ctx.clip()

      // draw zoomed-in clip
      ctx.drawImage(
        ctx_image.canvas, // source
        x - src_r, y - src_r, // sx, sy (source top-left corner of crop)
        2 * src_r, 2 * src_r, // sw, sh (width and height to copy)
        dst_x - dst_r, dst_y - dst_r, // dx, dy (destination position in target)
        2 * dst_r, 2 * dst_r, // dw, dh (draw size)
      )

      // draw cross on clip
      if(adjusted) {
        // marker is adjusted, draw cross
        const cross_x = dst_x + (new_x - x) * (dst_r / src_r)
        const cross_y = dst_y + (new_y - y) * (dst_r / src_r)
        Draw.drawCross(ctx, dst_x, dst_y, dst_r/4, '#4d88ffcc', 3)
        Draw.drawCross(ctx, cross_x, cross_y, dst_r*2, color+'cc', 3)
      }else if(!points) {
        // points are not defined, draw cross on center of the clip
        // used in video preview and calibration image
        Draw.drawCross(ctx, dst_x, dst_y, dst_r*2, color+'80', 3)
      }

      // remove clipping
      ctx.restore()

      // draw clip border
      Draw.drawCircle(ctx, dst_x, dst_y, dst_r, null, color, 3)

      // draw zoomed clip border
      Draw.drawCircle(ctx, x, y, src_r, null, color, 3)

      // draw navigation line between clip and zoomed clip
      const n1 = Algo.circleLineIntersection(x, y, src_r, dst_x, dst_y)
      const n2 = Algo.circleLineIntersection(dst_x, dst_y, dst_r, x, y)
      Draw.drawLine(ctx, ...n1, ...n2, color+'cc', 1)
    }

    return markers
  } // drawCalibrationMarkers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw calibration markers with zoomed clips onto canvas
  */
  static drawCalibrationLegend(ctx_image, calibration, identity, legend_lang) {
    const { width, height } = ctx_image.canvas
    const canvas = document.createElement('canvas')
    const legend_height = 128 // height of legends on top and bottom of image
    canvas.width = width
    canvas.height = height + 2 * legend_height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(ctx_image.canvas, 0, legend_height)

    const {
      calibration_dt,
      channel,
      mounting,
      calibrator,
      remark,
      radar,
      camera,
      lens,
      zoom,
      camera_yaw,
      camera_pitch,
      camera_roll,
      radar_yaw,
    } = calibration

    const { sn, version, checksum } = identity

    const version_parts = String(version??'').split('/')
    const version_str = version_parts[4]||'-'
    const date_str = _.usec2ld(calibration_dt)
    const time_str = _.usec2lt(calibration_dt)

    // draw legend
    const bg = '#29303d'
    Draw.drawRectangle(ctx, 0, 0, canvas.width, legend_height, bg)
    Draw.drawRectangle(ctx, 0, height + legend_height, canvas.width, legend_height, bg)

    const font_label = '19px Helvetica'
    const font_normal = 'bold 24px Helvetica'
    const font_large = 'bold 36px Helvetica'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#fff'

    let y = 0

    const write = (x, text, split_length, max_length) => {
      text = String(text??'').toUpperCase()
      let line1 = text
      let line2 = ''

      if(split_length>0 && text.length>split_length) {
        max_length = max_length > 0 ? max_length : split_length
        if(max_length>0 && text.length > max_length * 2) {
          text = text.slice(0, max_length * 2)
        }
        const [a, b] = _.splitTextHalf(text)
        if(a.length <= max_length && b.length <= max_length) {
          line1 = a
          line2 = b
        }else {
          line1 = text.slice(0, max_length)
          line2 = text.slice(max_length)
        }
      }

      if(line2 === '') {
        ctx.fillText(line1, x, y)
      }else {
        const line_height = parseInt(ctx.font.replace(/\D+/g, '')) / 2 + 1
        ctx.fillText(line1, x, y - line_height)
        ctx.fillText(line2, x, y + line_height)
      }
    }
    const writet = (x, text, split_length, max_length) => {
      text = LANG.translate(legend_lang, text)
      write(x, text, split_length, max_length)
    }

    const x = [120, 350, 590, 830, 1070, 1320, 1580, 1800]

    // display top legend
    y = 32
    ctx.font = font_label
    writet(x[0], 'dopplercalibration.sn')
    writet(x[1], 'dopplercalibration.version')
    writet(x[2], 'dopplercalibration.checksum')
    writet(x[3], 'dopplercalibration.radar')
    writet(x[4], 'dopplercalibration.channel')
    writet(x[5], 'dopplercalibration.camera')
    writet(x[6], 'dopplercalibration.lens')
    writet(x[7], 'dopplercalibration.zoom')

    y += 54
    ctx.font = font_normal
    write(x[0], sn)
    write(x[1], version_str)
    write(x[2], checksum)
    write(x[3], radar, 12, 14)
    writet(x[4], 'dopplercalibration.channel.'+channel, 12, 14)
    write(x[5], camera, 12, 14)
    write(x[6], lens, 12, 16)
    write(x[7], zoom.toFixed(2)+' x')

    // display bottom legend
    y = height + legend_height + 32
    ctx.font = font_label
    writet(x[0], time_str)
    writet(x[1], 'dopplercalibration.mounting')
    writet(x[2], 'dopplercalibration.calibrator')
    writet(x[3], 'dopplercalibration.remark')
    writet(x[4], 'dopplercalibration.radar_yaw')
    writet(x[5], 'dopplercalibration.camera_yaw')
    writet(x[6], 'dopplercalibration.camera_pitch')
    writet(x[7], 'dopplercalibration.camera_roll')

    y += 54
    ctx.font = font_large
    write(x[0], date_str)
    ctx.font = font_normal
    write(x[1], mounting ?? '', 12, 14)
    write(x[2], calibrator ?? '', 12, 14)
    write(x[3], remark ?? '', 12, 14)
    ctx.font = font_large
    write(x[4], radar_yaw.toFixed(2) + '°')
    write(x[5], camera_yaw.toFixed(2) + '°')
    write(x[6], camera_pitch.toFixed(2) + '°')
    write(x[7], camera_roll.toFixed(2) + '°')

    return canvas
  } // drawCalibrationLegend

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * draw calibration markers with zoomed clips onto canvas
  */
  static calibrationProperty(key, value) {
    let after
    let prefix
    let type = 'string'
    let numeric
    if(/_dt$/.test(key)) {
      type = 'datetimeus'
      numeric = parseInt(value)
    }else if(key === 'channel') {
      prefix = 'dopplercalibration.channel.'
      numeric = parseInt(value)
    }else if(key === 'zooms') {
      type = 'richtext'
      if(!value || typeof value!=='object') value = {}
      const levels = Object.keys(value).map(parseFloat).filter(f => !isNaN(f)).sort((a, b) => a - b)
      value = levels.map(z => `${z.toFixed(2)}x`).join(', ')
    }else if(/_height|_side|_forward|_lat2?$|_lon$/.test(key)) {
      after = 'm'
      numeric = parseFloat(value)
      value = numeric.toFixed(3)
    }else if(/yaw|pitch|roll|angle/.test(key)) {
      after = '°'
      numeric = parseFloat(value)
      value = numeric.toFixed(2)
    }else if(/time_shift/.test(key)) {
      after = 'ms'
      numeric = value = parseInt(value)
    }else if(/pixels|_x2?$|_y$/.test(key)) {
      after = 'px'
      numeric = value = parseInt(value)
    }else if(/zoom/.test(key)) {
      after = 'x'
      numeric = parseFloat(value)
      value = numeric.toFixed(2)
    }

    if(value === undefined || value === null || value === isNaN) value = undefined
    const string = String(value ?? '')
    let text

    if(prefix) {
      text = `{{${prefix}${value}}}`
    }else if(string === '') {
      text = '-'
    }else if(type === 'datetimeus') {
      text = `{{datetime:${value}}}`
    }else if(after) {
      text = `${string} ${after}`
    }else {
      text = string
    }

    return { key, value, numeric, string, text, type, after, prefix }
  } // calibrationProperty

  static serializePulses(pulses) {
    if(!Array.isArray(pulses) || pulses.length===0) return new Uint8Array(0)
    pulses = pulses.map(pulse => {
      const {
        event_dt,
        log_dt,
        id,
        channel,
        sog_signal,
        sog_speed,
        signal,
        speed,
        distance,
        trigger,
        raw,
      } = pulse
      return [
        event_dt,
        log_dt,
        id,
        channel,
        sog_signal,
        sog_speed,
        signal,
        speed,
        distance,
        trigger,
        raw,
      ]
    })
    return msgpack.encode(pulses)
  } // serializePulses

  static deserializePulses(pulses) {
    if(!pulses || !(pulses instanceof Uint8Array) || pulses.length===0) return []
    try {
      pulses = msgpack.decode(pulses)
      if(!Array.isArray(pulses)) return []
      pulses = pulses.map(pulse => {
        const [
          event_dt,
          log_dt,
          id,
          channel,
          sog_signal,
          sog_speed,
          signal,
          speed,
          distance,
          trigger,
          raw,
        ] = pulse
        return {
          event_dt,
          log_dt,
          id,
          channel,
          sog_signal,
          sog_speed,
          signal,
          speed,
          distance,
          trigger,
          raw,
        }
      })
      return pulses
    }catch(e) {
      return []
    }
  } // serializePulses

  static serializeNALs(nals) {
    if(!Array.isArray(nals) || nals.length===0) return new Uint8Array(0)
    nals = nals.map(nal => {
      const {
        event_dt,
        log_dt,
        id,
        channel,
        nal_type,
        payload,
      } = nal
      return [
        event_dt,
        log_dt,
        id,
        channel,
        nal_type,
        payload,
      ]
    })
    return msgpack.encode(nals)
  } // serializeNALs

  static deserializeNALs(nals) {
    if(!nals || !(nals instanceof Uint8Array) || nals.length===0) return []
    try {
      nals = msgpack.decode(nals)
      if(!Array.isArray(nals)) return []
      nals = nals.map(nal => {
        const [
          event_dt,
          log_dt,
          id,
          channel,
          nal_type,
          payload,
        ] = nal
        return {
          event_dt,
          log_dt,
          id,
          channel,
          nal_type,
          payload,
        }
      })
      return nals
    }catch(e) {
      return []
    }
  }

  static serializeGPSes(gpses) {
    if(!Array.isArray(gpses) || gpses.length===0) return new Uint8Array(0)
    gpses = gpses.map(rec => {
      const {
        event_dt,
        gps_dt,
        gps,
        speed,
        heading,
        satellites,
        fix,
      } = rec
      return [
        event_dt,
        gps_dt,
        gps,
        speed,
        heading,
        satellites,
        fix,
      ]
    })
    return msgpack.encode(gpses)
  } // serializeGPSes

  static deserializeGPSes(gpses) {
    if(!gpses || !(gpses instanceof Uint8Array) || gpses.length===0) return []
    try {
      gpses = msgpack.decode(gpses)
      if(!Array.isArray(gpses)) return []
      gpses = gpses.map(rec => {
        const [
          event_dt,
          gps_dt,
          gps,
          speed,
          heading,
          satellites,
          fix,
        ] = rec
        return {
          event_dt,
          gps_dt,
          gps,
          speed,
          heading,
          satellites,
          fix,
        }
      })
      return gpses
    }catch(e) {
      return []
    }
  } // deserializeGPSes

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * constructs event/hit image with desired artefacts and optional legends
  */
  static async getEventImage(message, artefacts, lang, q=0.9) {
    if(!message?.params || typeof message.params!=='object') return
    const { channel, event_dt, seq, identity, calibration, optics, limits, event, gps, alpr, jpeg } = message.params
    const result = message.action === 'mmDopplerHit' ? 'hit' : message.params?.result || '?'

    if(!(channel>0 && event_dt>0 && result)) return
    if(!identity || typeof identity!=='object') return
    if(!calibration || typeof calibration!=='object') return
    if(!optics || typeof optics!=='object') return
    if(!limits || typeof limits!=='object') return
    if(!event || typeof event!=='object') return
    if(!jpeg || !(jpeg instanceof Uint8Array || typeof jpeg === 'string') || jpeg.length===0) return

    let jpeg_us = parseInt(message.params?.jpeg_us)
    if(!(jpeg_us>0)) jpeg_us = parseInt(event?.shot_us) // fallback to shot_us for backward compatibility

    const rig = Algo.rig(calibration, optics)
    if(!rig) return

    let blob
    let img_data_url
    if(typeof jpeg === 'string') {
      img_data_url = jpeg
    }else if(jpeg instanceof Uint8Array) {
      blob = new Blob([jpeg], { type: 'image/jpeg' })
      img_data_url = URL.createObjectURL(blob)
    }
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    try {
      const img = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = img_data_url
      })

      // canvas is enlarged to make space for info and legend
      const { width, height } = img
      canvas.width = width
      canvas.height = height
      ctx.drawImage(img, 0, 0, width, height)
    }catch(e) {
      return
    }
    if(blob) {
      URL.revokeObjectURL(img_data_url)
      blob = null
    }

    const { camera_radar_side, camera_radar_forward, camera_height, project } = rig
    const { lateral } = event
    const { longitudal } = Doppler.estimatePosition(rig, event, jpeg_us, 0)

    // draw central cross
    if(artefacts?.cross) {
      const { width, height } = canvas
      const color = artefacts.cross?.color || '#ff44ff80'
      const thick = _.natural(artefacts.cross?.thick) || 3
      Draw.drawLine(ctx, 0, height/2, width, height/2, color, thick)
      Draw.drawLine(ctx, width/2, 0, width/2, height, color, thick)
    }

    // draw camera grid
    if(artefacts?.grid) {
      const color = artefacts.grid?.color || '#00ffff80'
      const thick1 = _.natural(artefacts.grid?.thick1) || 1
      const thick2 = _.natural(artefacts.grid?.thick2) || 2
      Doppler.drawGrid(ctx, rig, color, thick1, thick2)
    }

    // draw camera horizon
    if(artefacts?.horizon) {
      const color = artefacts.horizon?.color || '#0396df'
      const thick = artefacts.horizon?.thick || 10
      Doppler.drawHorizon(ctx, rig, color, thick)
    }

    const result_color = Doppler.color(result)

    // draw travel line
    const LAT = -(lateral + camera_radar_side)
    const GROUND = -camera_height
    if(artefacts?.travel) {
      const color = artefacts.travel?.color || result_color+'a0'
      const thick = artefacts.travel?.thick || 7
      const LON1 = 1
      const LON2 = 1000000
      const p1 = project([LON1, LAT, GROUND], true)
      const p2 = project([LON2, LAT, GROUND], true)
      Draw.drawLine(ctx, ...p1, ...p2, color, thick)
    }

    // for debugging, show also position as would be estimated from radar without ALPR fix
    const { fix_longitudal } = event
    if(artefacts.longitudal_diff && (fix_longitudal > 0 || fix_longitudal < 0)) {
      // draw middle line
      if(artefacts?.mid) {
        const color = '#000000cc'
        const thick = artefacts.mid?.thick || 14
        const HEIGHT1 = GROUND + 0.2
        const HEIGHT2 = GROUND + Doppler.VEHICLE_HEIGHT - 0.2
        const LON = longitudal + parseFloat(camera_radar_forward) - fix_longitudal
        const p1 = project([LON, LAT, HEIGHT1], true)
        const p2 = project([LON, LAT, HEIGHT2], true)
        Draw.drawLine(ctx, ...p1, ...p2, color, thick)
      }

      // draw boundary corners
      if(artefacts?.corners) {
        const color = '#000000cc'
        const thick = artefacts.corners?.thick || 7
        event.start_longitudal -= fix_longitudal
        const rect = Doppler.getBoundaries(rig, event, jpeg_us, 0)
        event.start_longitudal += fix_longitudal
        const corner_size = 80
        Draw.drawCorners(ctx, rect, corner_size, color, thick)
      }
    }

    // draw middle line
    if(artefacts?.mid) {
      const color = artefacts.mid?.color || result_color+'cc'
      const thick = artefacts.mid?.thick || 14
      const HEIGHT1 = GROUND + 0.2
      const HEIGHT2 = GROUND + Doppler.VEHICLE_HEIGHT - 0.2
      const LON = longitudal + parseFloat(camera_radar_forward)
      const p1 = project([LON, LAT, HEIGHT1], true)
      const p2 = project([LON, LAT, HEIGHT2], true)
      Draw.drawLine(ctx, ...p1, ...p2, color, thick)
    }

    // draw boundary corners
    if(artefacts?.corners) {
      const color = artefacts.corners?.color || result_color+'cc'
      const thick = artefacts.corners?.thick || 7
      const rect = Doppler.getBoundaries(rig, event, jpeg_us, 0)
      const corner_size = 80
      Draw.drawCorners(ctx, rect, corner_size, color, thick)
    }

    // draw ALPR license plate
    let alpr_position = null
    if(artefacts?.alpr && alpr?.lpn) {
      const bg = artefacts.alpr?.bg || '#00000080'
      const color = artefacts.alpr?.color || '#ffffff'
      const thick = artefacts.alpr?.thick || 0
      const border = artefacts.alpr?.border || '#000000'
      const offset = artefacts.alpr?.offset || 0.01 * canvas.width
      let width = artefacts.alpr?.width
      if(/%$/.test(width)) {
        width = parseInt(width) / 100 * canvas.width
      }
      if(!(width > 0)) {
        width = 0.20 * canvas.width
      }

      const x = canvas.width - 1 - offset - width
      const y1 = offset
      const height1 = 0.05 * canvas.width
      let height = height1

      Draw.drawRectangle(ctx, x, y1, width, height1, bg)
      const font_size = width / 8
      const font = artefacts.alpr?.font || 'Helvetica'
      Draw.drawText(ctx, alpr.lpn, font_size, x + width / 2, y1 + height1 / 2, 'center', color, color, 1, font)

      alpr_position = { x, y: y1, width, height }

      try {
        const img = await new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = () => reject(new Error('Failed to load image'))
          const uri = String(alpr?.base64 || '')
          img.src = uri.startsWith('data:') ? uri : `data:image/jpeg;base64,${uri}`
        })

        if(img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          const height2 = img.naturalHeight * (width / img.naturalWidth)
          height += height2
          ctx.drawImage(img, x, y1 + height1, width, height2)
          alpr_position.height = height
        }
      }catch(e) {
        // ignore
      }

      if(thick > 0) {
        Draw.drawRectangle(ctx, x-thick, y1-thick, width+thick*2, height+thick*2, null, border, thick)
      }
    }

    // end processing if no legend is requested
    if(!lang) {
      const src = canvas.toDataURL('image/jpeg', q)
      return { src, alpr_position }
    }

    // draw legends
    const off = lang ? 128 : 0 // height of legends on top and bottom of image
    const canvas2 = document.createElement('canvas')
    canvas2.width = canvas.width
    canvas2.height = canvas.height + 2 * off
    const ctx2 = canvas2.getContext('2d')
    ctx2.drawImage(canvas, 0, off)
    if(alpr_position) {
      // shift ALPR position for legend canvas
      alpr_position.y += off
    }

    // draw background rectangles for top and bottom legends
    const result_fg = Doppler.foreground(result)
    const result_bg = Doppler.background(result)
    Draw.drawRectangle(ctx2, 0, 0, canvas2.width, off, result_bg)
    Draw.drawRectangle(ctx2, 0, canvas2.height-off, canvas2.width, off, result_bg)

    // prepare fonts
    const font1 = 'Open Sans Regular'
    const font1data = require('../../res/fonts/open-sans/OpenSans-Regular-webfont.woff?raw')
    const font2 = 'Open Sans Bold'
    const font2data = require('../../res/fonts/open-sans/OpenSans-Bold-webfont.woff?raw')

    const fonts = [...document.fonts]
    if(!fonts.includes(font1)) {
      await Draw.loadFont(font1, font1data)
    }
    if(!fonts.includes(font2)) {
      await Draw.loadFont(font2, font2data)
    }

    const tr = (x) => {
      return LANG.translate(lang, x).toUpperCase()
    }

    // display top legend
    const { sn, version, checksum } = identity
    const { sog_speed } = event
    const zoom = parseFloat(optics?.zoom).toFixed(1)
    const version_parts = String(version??'').split('/')
    const version_str = version_parts[4]||'-'
    const lat_lng = toLatLng(gps)
    const gps_str = lat_lng ? `${lat_lng[0].toFixed(5)}°, ${lat_lng[1].toFixed(5)}°` : '-'
    const seq_str = seq>0 ? String(seq).padStart(6, '0') : '-'
    const sog_str = sog_speed>=0 ? `${parseFloat(sog_speed*3.6).toFixed(1)} km/h` : '-'

    // setup fonts according to canvas size
    const h = canvas.height
    const font_label = `${_.roundBy(h/45, 1)}px "${font1}"` // 24px on 1080p
    const font_normal = `${_.roundBy(h/28, 1)}px "${font2}"` // 42px on 1080p
    const font_small = `${_.roundBy(h/40, 1)}px  "${font2}"` // 30px on 1080p

    ctx2.textAlign = 'center'
    ctx2.textBaseline = 'middle'
    ctx2.fillStyle = result_fg
    ctx2.font = font_label
    let y = 32
    const dy = 54
    ctx2.fillText(tr('doppler.seq'), 110, y)
    ctx2.fillText(tr('doppler.gps'), 360, y)
    ctx2.fillText(tr('doppler.sog'), 655, y)
    ctx2.fillText(tr('doppler.sn'), 963, y)
    ctx2.fillText(tr('doppler.version'), 1240, y)
    ctx2.fillText(tr('doppler.checksum'), 1540, y)
    ctx2.fillText(tr('doppler.zoom'), 1820, y)
    y += dy
    ctx2.font = font_normal
    ctx2.fillText(seq_str, 110, y)
    ctx2.font = font_small
    ctx2.fillText(gps_str, 360, y)
    ctx2.font = font_normal
    ctx2.fillText(sog_str, 655, y)
    ctx2.fillText(sn, 963, y)
    ctx2.fillText(version_str, 1240, y)
    ctx2.fillText(checksum, 1540, y)
    ctx2.fillText(zoom + ' x', 1820, y)

    // display bottom legend
    const { speed, direction, lane } = event
    const { speed_limit, hit_limit } = limits
    const date_str = _.usec2ld(jpeg_us)
    const time_str = _.usec2lt(jpeg_us)
    const limit_kmh = `${speed_limit} / ${hit_limit} km/h`
    const speed_kmh = (Math.floor(Math.abs(speed*3.6)*10)/10).toFixed(1) + ' km/h'

    y += 1080 + off - dy
    ctx2.font = font_label
    ctx2.fillText(date_str, 150, y)
    ctx2.fillText(tr('doppler.result'), 450, y)
    ctx2.fillText(tr('doppler.measured_speed'), 746, y)
    ctx2.fillText(tr('doppler.speed_hit_limit'), 1134, y)
    ctx2.fillText(tr('doppler.lane'), 1448, y)
    ctx2.fillText(tr('doppler.direction'), 1740, y)

    y += dy
    ctx2.font = font_normal
    ctx2.fillText(time_str, 150, y)

    const result_str = tr('doppler.result.'+result).toUpperCase()
    if(/\s/.test(result_str)) {
      const [first, second] = _.splitTextHalf(result_str)
      ctx2.font = font_small
      const line_height = parseInt(font_small.replace(/\D+/g, '')) / 2 + 1
      ctx2.fillText(first, 450, y - line_height)
      ctx2.fillText(second, 450, y + line_height)
      ctx2.font = font_normal
    }else {
      ctx2.fillText(result_str, 450, y)
    }

    ctx2.fillText(speed_kmh, 746, y)
    ctx2.fillText(limit_kmh, 1134, y)
    ctx2.fillText(lane, 1448, y)
    ctx2.font = font_small
    ctx2.fillText(tr('doppler.direction.'+direction), 1740, y)

    const src = canvas2.toDataURL('image/jpeg', q)
    return { src, alpr_position }
  } // getEventImage
} // Doppler

if(typeof module!=='undefined') { module.exports = Doppler } else if(typeof window!=='undefined') { window.Doppler = Doppler }
